import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { ShiftEventCrew } from "@/features/shift/model/Shift/Shift";
import { ShiftSystem } from "@/features/shift/model/Shift/ShiftConfiguration";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
	ShiftName,
	ShiftNameRequest,
	ShiftSettingsResponse,
	ShiftSystemRequest,
} from "@/model/Application/Settings/SettingsResponseTypes";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { ShiftQueryKeys } from "@/service/ShiftHomeQueries";

export const useMutateUpdateShiftSettings = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ShiftSettingsResponse) =>
			SATOSPortal.UpdateShiftSettings(request),
		onMutate: () => {
			const ShiftSettingsKey = [ApplicationQueryKeys.shiftSettings];
			void queryClient.cancelQueries({ queryKey: ShiftSettingsKey });

			const previousValue: ShiftSettingsResponse | undefined =
				queryClient.getQueryData(ShiftSettingsKey);

			if (previousValue) {
				queryClient.setQueryData<ShiftSettingsResponse>(ShiftSettingsKey, {
					...previousValue,
				});
			}

			return previousValue;
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ApplicationQueryKeys.shiftSettings],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useMutateCreateShiftName = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: createShiftName } = useMutation({
		mutationFn: (request: ShiftNameRequest) =>
			SATOSPortal.CreateShiftName(request),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftNames],
			});
		},
	});

	return { createShiftName };
};

// useMutateCreateShiftCrew.ts (or where it lives)
export const useMutateCreateShiftCrew = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createShiftCrew } = useMutation({
		mutationFn: (request: Parameters<typeof SATOSPortal.CreateShiftCrew>[0]) =>
			SATOSPortal.CreateShiftCrew(request),
		onSuccess: (created: ShiftEventCrew) => {
			queryClient.setQueryData<ShiftEventCrew[]>(
				[ShiftQueryKeys.shiftCrews],
				(old = []) => {
					const exists = old.some((row) => row.Id === created.Id);
					if (exists) {
						return old.map((row) => (row.Id === created.Id ? created : row));
					}
					return [...old, created];
				},
			);
		},
		onError: (err) => {
			console.error("CreateShiftCrew failed", err);
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftCrews],
			});
		},
	});

	return { createShiftCrew };
};

export const useMutateUpdateShiftCrew = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateShiftCrew } = useMutation({
		mutationFn: (request: ShiftName) =>
			SATOSPortal.UpdateShiftCrew(request.Id, request),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftCrews],
			});
		},
	});

	return { updateShiftCrew };
};

export const useMutateUpdateShiftName = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateShiftName } = useMutation({
		mutationFn: (request: ShiftName) =>
			SATOSPortal.UpdateShiftName(request.Id, request),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftNames],
			});
		},
	});

	return { updateShiftName };
};

export const useMutateDeleteShiftName = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: deleteShiftName } = useMutation({
		mutationFn: (id: number) => SATOSPortal.DeleteShiftName(id),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftNames],
			});
		},
	});

	return { deleteShiftName };
};

export const useMutateDeleteShiftCrew = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: deleteShiftCrew } = useMutation({
		mutationFn: (id: number) => SATOSPortal.DeleteShiftCrew(id),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftCrews],
			});
		},
	});

	return { deleteShiftCrew };
};

export const useMutateCreateShiftSystem = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: createShiftSystem } = useMutation({
		mutationFn: (request: ShiftSystemRequest) =>
			SATOSPortal.CreateShiftSystem(request),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftSystems],
			});
		},
	});

	return { createShiftSystem };
};

export const useMutateUpdateShiftSystem = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: updateShiftSystem } = useMutation({
		mutationFn: (request: ShiftSystem) =>
			SATOSPortal.UpdateShiftSystem(request.Id, { Name: request.Name }),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftSystems],
			});
		},
	});

	return { updateShiftSystem };
};

export const useMutateDeleteShiftSystem = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: deleteShiftSystem } = useMutation({
		mutationFn: (id: number) => SATOSPortal.DeleteShiftSystem(id),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [ShiftQueryKeys.shiftSystems],
			});
		},
	});

	return { deleteShiftSystem };
};
